import React, { useState } from 'react';
import { AppProvider, useAppContext } from './context/AppContext';
import Dashboard from './components/Dashboard';
import PaymentManager from './components/PaymentManager';
import PotManager from './components/PotManager';
import PaydayRules from './components/PaydayRules';
import ForecastChart from './components/ForecastChart';

const AppContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'forecast' | 'payments' | 'pots' | 'payday'>('dashboard');
  const { state, dispatch } = useAppContext();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'forecast':
        return <ForecastChart />;
      case 'payments':
        return <PaymentManager />;
      case 'pots':
        return <PotManager />;
      case 'payday':
        return <PaydayRules />;
      default:
        return <Dashboard />;
    }
  };

  const handleExport = () => {
    const dataToExport = {
      ...state,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `pennies-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);

        // Validate the imported data structure
        if (importedData.account && importedData.recurringPayments && importedData.oneOffPayments &&
            importedData.pots && importedData.paydayRules) {
          // Remove export metadata before importing
          const { exportDate, version, ...cleanData } = importedData;
          dispatch({ type: 'LOAD_STATE', payload: cleanData });
          alert('Data imported successfully!');
        } else {
          alert('Invalid file format. Please select a valid Pennies backup file.');
        }
      } catch (error) {
        alert('Error reading file. Please ensure it\'s a valid JSON file.');
      }
    };
    reader.readAsText(file);

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="app-title text-3xl">🪙🪙 Pennies</h1>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExport}
                className="bg-white text-green-600 border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                title="Export data"
              >
                <span>💾</span>
                <span>Export</span>
              </button>
              <button
                onClick={handleImportClick}
                className="bg-white text-green-600 border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                title="Import data"
              >
                <span>📂</span>
                <span>Import</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
      </header>

        <nav className="navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 nav-container">
            <div className="flex space-x-1 nav-tabs-wrapper">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              >
                📊 Dashboard
              </button>
              <button
                onClick={() => setActiveTab('forecast')}
                className={`nav-tab ${activeTab === 'forecast' ? 'active' : ''}`}
              >
                📈 Forecast
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`nav-tab ${activeTab === 'payments' ? 'active' : ''}`}
              >
                💳 Payments
              </button>
              <button
                onClick={() => setActiveTab('pots')}
                className={`nav-tab ${activeTab === 'pots' ? 'active' : ''}`}
              >
                🏺 Pots
              </button>
              <button
                onClick={() => setActiveTab('payday')}
                className={`nav-tab ${activeTab === 'payday' ? 'active' : ''}`}
              >
                💸 Payday
              </button>
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {renderContent()}
          </div>
        </main>
      </div>
  );
};

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
